import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignUpModal from './SignUpModal'; // Import your existing modal

const LoginPage = ({ setUser }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [error, setError] = useState('');
    const [showSignUpModal, setShowSignUpModal] = useState(false); // New state for modal

    const navigate = useNavigate();

    const baseURL = process.env.REACT_APP_BACKEND_URL

    // Toggle between login and sign-up modes
    const handleToggleAuth = () => {
        setIsLogin(!isLogin);
    };

    // Function to actually perform the signup/login
    const performAuth = async () => {
        setError('');
        setIsLoading(true);

        try {
            const endpoint = isLogin
                ? baseURL + '/api/user/validate-user'
                : baseURL + '/api/user/create-new-user';

            const payload = isLogin
                ? { email, password }
                : { name, email, password };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || 'Something went wrong.');
            }

            // Save user data to localStorage
            localStorage.setItem('token', data.token);
            localStorage.setItem('user_id', data.user_id);
            localStorage.setItem('name', data.name);
            localStorage.setItem('email', email);

            // Update global user state
            setUser({
                token: data.token,
                id: data.user_id,
                name: data.name,
                email: email
            });

            // Navigate to home after login/signup
            navigate('/');
        } catch (err) {
            setError(err.message || 'Login/Signup failed. Please try again.');
            console.error('Auth error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Show modal first if it's a sign-up, then proceed
        if (!isLogin) {
            // setShowSignUpModal(true);
            await performAuth();
        } else {
            // For login, proceed directly
            await performAuth();
        }
    };

    // Handle modal close and continue with signup
    const handleModalClose = async (shouldProceed = false) => {
        setShowSignUpModal(false);
        if (shouldProceed && !isLogin) {
            await performAuth();
        }
    };

    return (
        <>
            <style>{`
                /* --- Font Imports & CSS Variables (should be in a global stylesheet) --- */
                @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&family=Lato:wght@400;700&display=swap');

                :root {
                    --bg-light: #F7FAFC;
                    --primary: #2C5282;
                    --accent: #DD6B20;
                    --text-dark: #2D3748;
                    --card-light: #FFFFFF;
                    --font-heading: 'Merriweather', serif;
                    --font-body: 'Lato', sans-serif;
                    --error-light: #FED7D7;
                    --error-dark: #9B2C2C;
                }

                .info-button {
                    // position: ;
                    top: 2rem;
                    right: 2rem;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: #e77e2e21;
                    color: #e77e2e;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    font-weight: bold;
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .info-button:hover {
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(221, 107, 32, 0.4);
                }

                /* --- Login Page Styles --- */
                .login-page-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background-color: var(--bg-light);
                    font-family: var(--font-body);
                    padding: 2rem;
                    box-sizing: border-box;
                }
                .login-card {
                    background-color: var(--card-light);
                    padding: 2.5rem 3rem;
                    border-radius: 12px;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -2px rgba(0,0,0,0.04);
                    width: 100%;
                    max-width: 450px;
                    border: 1px solid #E2E8F0;
                }
                .login-title {
                    font-family: var(--font-heading);
                    font-size: 2rem;
                    font-weight: 900;
                    color: var(--primary);
                    text-align: center;
                    margin: 0 0 1.5rem 0;
                }
                .login-error {
                    color: var(--error-dark);
                    background-color: var(--error-light);
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    text-align: center;
                    margin-bottom: 1.5rem;
                    font-weight: 700;
                }
                .login-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1.25rem;
                }
                .form-input {
                    width: 100%;
                    padding: 0.9rem 1rem;
                    font-size: 1rem;
                    font-family: var(--font-body);
                    border: 1px solid #CBD5E0;
                    border-radius: 8px;
                    transition: border-color 0.3s, box-shadow 0.3s;
                    box-sizing: border-box;
                }
                .form-input:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px rgba(44, 82, 130, 0.2);
                }
                .submit-button {
                    font-size: 1.05rem;
                    font-weight: 700;
                    padding: 0.9rem 2.5rem;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    border: 2px solid transparent;
                    background-color: var(--accent);
                    color: var(--text-dark);
                    margin-top: 0.5rem;
                }
                .submit-button:hover {
                     transform: translateY(-3px);
                     box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                 .submit-button:disabled {
                    background-color: #A0AEC0;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                .toggle-auth-button {
                    background: none;
                    border: none;
                    color: var(--primary);
                    font-weight: 700;
                    cursor: pointer;
                    margin-top: 1.5rem;
                    text-align: center;
                    width: 100%;
                    padding: 0.5rem;
                }
                .toggle-auth-button:hover {
                    text-decoration: underline;
                }
            `}</style>

            <div className="login-page-container">
                <div className="login-card">
                    <h1 className="login-title">{isLogin ? 'Welcome Back!' : 'Create an Account'}</h1>
                    {error && (
                        <div className="login-error">{error}</div>
                    )}
                    <form onSubmit={handleSubmit} className="login-form">
                        {!isLogin && (
                            <input
                                type="text"
                                placeholder="Full Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="form-input"
                            />
                        )}
                        <input
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="form-input"
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="form-input"
                        />
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isLoading}
                        >
                            {isLoading
                                ? (isLogin ? 'Logging In...' : 'Signing Up...')
                                : (isLogin ? 'Log In' : 'Sign Up')}
                        </button>
                    </form>
                    <button onClick={handleToggleAuth} className="toggle-auth-button">
                        {isLogin
                            ? "Don't have an account? Sign Up"
                            : "Already have an account? Log In"}
                    </button>
                </div>

                {/* Sign-up Modal - shows when user clicks Sign Up button
                {showSignUpModal && (
                    <SignUpModal
                        isOpen={showSignUpModal}
                        onClose={handleModalClose}
                    />
                )}

                {showInfoModal && (
                    <SignUpModal
                        isOpen={showInfoModal}
                        onClose={() => setShowInfoModal(false)}
                    />
                )} */}
            </div>
        </>
    );
};

export default LoginPage;